import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Icons, Images } from '../../../../assets';
import { AccountContext, AccountContextInterface } from '../../../../contexts';
import { SKILL_TYPE, UserSkill } from '../../../../models/skill.model';
import { Tab } from '../../../../types';
import { formatEnumValue } from '../../../../utils';

interface SkillOverViewCardProps {
  accountId: string;
  onSkillClick: (skill: UserSkill, type: Tab.Creds | Tab.Reccos) => void;
}

const SkillOverviewCard: FC<SkillOverViewCardProps> = ({ accountId, onSkillClick }) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [selectWidth, setSelectWidth] = useState<number>(80);
  const [filterType, setFilterType] = useState<'' | SKILL_TYPE>('');
  const [skills, setSkills] = useState<UserSkill[]>([]);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setLoading(true);
        const { data } = await accountService.getUserSkills(accountId, 1, 10, filterType || undefined);
        if (data) {
          const updated = data.map((skill: UserSkill) => ({
            ...skill,
            ratingInPercentage:
              (skill.ratingInPercentage > 0 ? skill.ratingInPercentage : 40) + skill.credCount * 10 + skill.reccoCount * 5,
          }));
          setSkills(updated);
        }
      } catch (error) {
        console.error('Failed to fetch user skills', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSkills();
  }, [accountId, accountService, filterType]);

  useEffect(() => {
    if (spanRef.current) {
      setSelectWidth(spanRef.current.offsetWidth + 16);
    }
  }, [filterType]);

  useEffect(() => {
    if (!skills.length || loading) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current?.clientWidth || 300;
    const height = svgRef.current?.clientHeight || 300;
    const padding = 16;

    const pack = d3
      .pack<UserSkill>()
      .size([width - padding * 2, height - padding * 2])
      .padding(-4);

    const root = d3
      .hierarchy<{ children: UserSkill[] } | UserSkill>({ children: skills })
      .sum(d => Number((d as UserSkill).ratingInPercentage) || 0);

    const nodes = pack(root as d3.HierarchyNode<UserSkill>).leaves();
    const color = d3.scaleOrdinal<string, string>(d3.schemePastel1);

    svg.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');

    const node = svg
      .selectAll<SVGGElement, d3.HierarchyCircularNode<UserSkill>>('g')
      .data(nodes)
      .join('g')
      .attr('transform', d => `translate(${d.x + padding},${d.y + padding})`);

    node
      .append('circle')
      .attr('r', d => d.r)
      .attr('fill', (_, i) => color(i.toString()))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('click', (_, d) => {
        onSkillClick(d.data, Tab.Creds);
      })
      .on('mousemove', event => {
        const tooltip = tooltipRef.current;
        if (tooltip) {
          tooltip.style.left = `${event.pageX + 10}px`;
          tooltip.style.top = `${event.pageY + 10}px`;
        }
      })
      .on('mouseleave', () => {
        const tooltip = tooltipRef.current;
        if (tooltip) {
          tooltip.style.display = 'none';
        }
      });

    node
      .append('clipPath')
      .attr('id', d => `clip-${d.data.id}`)
      .append('circle')
      .attr('r', d => d.r - 2);

    node
      .append('text')
      .text(d => {
        const maxChars = Math.floor((d.r - 4) / 4);
        const name = d.data?.skill?.name || '';
        return name.length > maxChars ? name.slice(0, maxChars - 1) + '…' : name;
      })
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('clip-path', d => `url(#clip-${d.data.id})`)
      .style('font-size', d => `${Math.min((d.r - 4) / 3, 14)}px`)
      .style('pointer-events', 'none')
      .style('fill', '#000');

    node.append('title').text(d => d.data?.skill?.name || '');
  }, [loading, skills, onSkillClick]);

  if (!loading && filterType === '' && skills.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex gap-2 items-center justify-between mb-4">
        <div className="flex gap-2 items-center">
          <img alt="overview" className="h8 w-8" src={Icons.OVERVIEW} />
          <p className="font-semibold text-xl text-[#343A40]">Overview</p>
        </div>
        <span ref={spanRef} className="absolute invisible px-3 py-1 text-sm whitespace-nowrap">
          {!filterType ? 'Skills' : filterType}
        </span>
        <div className="relative z-10">
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value as '' | SKILL_TYPE)}
            className="appearance-none bg-white border border-black/20 pl-3 py-1 rounded-full shadow-sm text-black text-xs focus:outline-none"
            style={{ width: selectWidth }}>
            <option value="">Skills</option>
            <option value={SKILL_TYPE.HARD_SKILL}>Hard Skills</option>
            <option value={SKILL_TYPE.SOFT_SKILL}>Soft Skills</option>
          </select>
          <div className="absolute pointer-events-none right-3 text-gray-600 text-xs top-1/2 -translate-y-1/2">▼</div>
        </div>
      </div>
      <div className="bg-[#F5F5F7] h-80 rounded-2xl w-full overflow-visible">
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute z-50 hidden rounded-sm bg-black/80 px-2 py-1 text-xs text-white shadow-sm"></div>
        {loading && (
          <div className="items-center justify-center p-4" role="status">
            <img alt="Loading Skills" src={Images.SKELETON_BUBBLES} className="animate-pulse w-full" />
          </div>
        )}
        {!loading && skills.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 text-sm gap-2 text-center px-4">
            <span className="text-lg font-medium">{`No ${formatEnumValue(filterType)}`}</span>
          </div>
        )}
        {!loading && skills.length > 0 && <svg ref={svgRef} className="h-full w-full" />}
      </div>
    </>
  );
};

export default SkillOverviewCard;
